import { Layout, theme } from "antd";
import React, { useMemo, useState } from "react";
import { Route, Routes, Link } from "react-router-dom";

import Error404 from "../pages/errors/Error404";
import MobileMenuDrawer from "./MobileMenuDrawer";
import Top from "./Top";
import routes, { AppRoute } from "./routes";
import SidebarMenu from "./SidebarMenu";

// import AdminProfile from "../pages/Profile";
// import RecipientsMainComp from "../pages/recipients/RecipientsMainComp";
// import ManageUsersMainComp from "../pages/users/ManageUsersMainComp";
// import SubAdminMainComp from "../pages/subAdmin/SubAdminMainComp";
// import MainServiceComp from "../pages/website/services/MainServiceComp";
// import MainTeamComp from "../pages/website/team/MainTeamComp";
// import BlogsMainComp from "../pages/blogs/BlogsMainComp";
// import ReportsLibraryMainComp from "../pages/reports/ReportsLibraryMainComp";

import { useUserStore } from "../../store/slices/user";
import { IUserInfo } from "@src/types/api/user";

export function hasPermission(
  user: IUserInfo | null | undefined,
  module?: string,
  action?: string,
  superAdminOnly?: boolean,
) {
  if (!user) return false;

  // SUPER_ADMIN bypasses every permission check, always.
  if (user.user_type === "SUPER_ADMIN") return true;

  if (superAdminOnly) return false;

  // Route does not require permission
  if (!module || !action) {
    return true;
  }

  return user.permissions?.some(
    (p) => p.module === module && p.action === action,
  );
}

const ProtectedRoute = ({ component: Component, permission, superAdminOnly }: any) => {
  const { user } = useUserStore();

  if (!hasPermission(user, permission?.module, permission?.action, superAdminOnly)) {
    return <Error404 />;
  }

  return <Component />;
};

const MyLayout: React.FC = () => {
  const [open, setOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(false);

  const { user } = useUserStore();

  const {
    token: { colorBgContainer },
  } = theme.useToken();

  const showDrawer = () => {
    setOpen(true);
  };

  // ✅ Sidebar routes (KEEP children for dropdown)
  const allowedRoutes = useMemo(() => {
    if (!user) return [];

    return routes.filter(
      (route) =>
        !route.hidden &&
        hasPermission(
          user,
          route.permission?.module,
          route.permission?.action,
          route.superAdminOnly,
        ),
    );
  }, [user]);

  // ✅ Flatten + Filter routes for React Router
  const allowedFlatRoutes = useMemo(() => {
    if (!user) return [];

    const temp: AppRoute[] = [];

    function flatten(arr: AppRoute[], baseUrl: string = "") {
      arr.forEach((route) => {
        const fullUrl = `${baseUrl}${route.url}`;

        const allowed = hasPermission(
          user,
          route.permission?.module,
          route.permission?.action,
          route.superAdminOnly,
        );

        if (!allowed) return;

        if (route.children) {
          flatten(route.children, fullUrl);
        } else {
          temp.push({
            ...route,
            url: fullUrl.replace(/\/+/g, "/"),
          });
        }
      });
    }

    flatten(routes);

    return temp;
  }, [user]);

  return (
    <Layout className="min-h-screen  bg-slate-50">
      {/* Sidebar */}
      <Layout.Sider
        collapsible
        width={260}
        collapsed={collapsed}
        trigger={null}
        className="sm:block hidden"
        style={{
          overflow: 'auto',
          height: '100vh',
          position: 'sticky',
          top: 0,
          left: 0,
        }}
      >
        <SidebarMenu collapsed={collapsed} routes={allowedRoutes} />
      </Layout.Sider>

      {/* Mobile Drawer */}
      <MobileMenuDrawer open={open} setOpen={setOpen} routes={allowedRoutes} />

      {/* Main Layout */}
      <Layout className="bg-slate-50 min-h-screen flex flex-col flex-1">
        <Top {...{ collapsed, setCollapsed, showDrawer }} />

        <Layout.Content className="p-3 sm:p-5 text-black flex-1 min-h-screen w-full">
          <Routes>
            {allowedFlatRoutes.map((v) =>
              v.component && v.url ? (
                <Route
                  path={v.url}
                  key={v.url}
                  element={
                    <ProtectedRoute
                      component={v.component}
                      permission={v.permission}
                      superAdminOnly={v.superAdminOnly}
                    />
                  }
                />
              ) : null,
            )}

            {/* ✅ Static Routes */}
            <Route path="*" element={<Error404 />} />
          </Routes>
        </Layout.Content>
      </Layout>
    </Layout>
  );
};

export default MyLayout;
